import React, { useState } from 'react';
import { Edit2 as Edit, X as Clear, Save } from 'react-feather';

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material';
import { DeleteItemConfirmModal } from './DeleteItemConfirmModal';
import { DeleteIcon } from './icons/DeleteIcon';
import { Section } from './Section';

const EditControls = ({
  name,
  editing,
  setEditing,
  onClear,
  onSave,
  loading,
  create,
  onDelete,
  disableDelete,
  deleteConfirmText,
}) => {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const onClearAndEdit = () => {
    if (onClear) {
      onClear();
    }
    setEditing(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setConfirmModalOpen(false);
    setEditing(false);
  };

  const saveAndMaybeChangeMode = () => {
    let editMode = false;
    if (onSave) {
      if (onSave() === false) {
        editMode = true;
      }
    }
    setEditing(editMode);
  };

  const width = 18;
  const height = width;
  const saveButtonClass = name ? `${name}-saveButton` : 'saveButton';
  const editButtonClass = name ? `${name}-editButton` : 'editButton';
  const clearButtonClass = name ? `${name}-clearButton` : 'clearButton';
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="flex-end"
      alignItems="center"
      width="100%"
    >
      {loading && <CircularProgress style={{ width, height }} />}
      {!loading && editing && !create && onDelete && !disableDelete && (
        <Button
          variant="text"
          label="Delete"
          onClick={() => setConfirmModalOpen(true)}
          size="small"
          startIcon={<DeleteIcon size={16} />}
        >
          Delete
        </Button>
      )}
      {!loading && editing && (
        <Button
          variant="contained"
          color="secondary"
          className={saveButtonClass}
          onClick={saveAndMaybeChangeMode}
          size="small"
          startIcon={<Save size={16} />}
        >
          Save
        </Button>
      )}
      {!loading && editing && !create && (
        <Button
          variant="outlined"
          color="secondary"
          className={clearButtonClass}
          onClick={onClearAndEdit}
          size="small"
          startIcon={<Clear size={16} />}
        >
          Cancel
        </Button>
      )}
      {!loading && !editing && (
        <Button
          variant="outlined"
          color="secondary"
          className={editButtonClass}
          onClick={() => setEditing(true)}
          size="medium"
          startIcon={<Edit size={16} />}
        >
          Edit
        </Button>
      )}
      <DeleteItemConfirmModal
        onConfirm={handleDelete}
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        deleteType={name}
        headerText={`Are you sure you want to delete this ${name}?`}
        bodyText={deleteConfirmText}
      />
    </Stack>
  );
};

export const EditablePanel = ({
  children,
  name,
  loading = false,
  onClear,
  onSave,
  onDelete,
  disableDelete = true,
  create,
  editable = true,
  title,
  maxWidth = '100%',
  deleteConfirmText = '',
}) => {
  const [editing, setEditing] = useState(false);
  return (
    <Section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '16px',
          boxShadow: '0px -1px 0px 0px rgba(0, 0, 0, 0.10) inset',
        }}
      >
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h6">{title}</Typography>
        </div>

        {editable && (
          <div
            style={{
              flexShrink: 1,
            }}
          >
            <EditControls
              name={name}
              editing={editing || create}
              setEditing={setEditing}
              onClear={onClear}
              onSave={onSave}
              onDelete={onDelete}
              disableDelete={disableDelete}
              loading={loading}
              create={create}
              deleteConfirmText={deleteConfirmText}
            />
          </div>
        )}
      </div>
      <Box
        sx={{
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: { maxWidth } }}>
          {children({ editing: editing || create })}
        </Box>
      </Box>
    </Section>
  );
};
